package com.ijoin.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.AccountBalanceWallet
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.ChevronRight
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.PersonAdd
import androidx.compose.material.icons.filled.Settings
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.CenterAlignedTopAppBar
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import coil3.compose.AsyncImage
import com.ijoin.data.model.Event
import com.ijoin.data.model.MoneyAmount
import com.ijoin.data.model.User
import com.ijoin.data.model.UserProfile
import com.ijoin.getPlatform
import com.ijoin.i18n.Strings
import com.ijoin.i18n.strings
import com.ijoin.platform.isWebMobile
import com.ijoin.platform.shortcut.ProfileShortcutInfo
import com.ijoin.platform.shortcut.ProfileShortcutManager
import com.ijoin.ui.components.EventManagementButtons
import com.ijoin.ui.components.MainBottomNavigationBar
import com.ijoin.ui.components.RecommendedEventCard
import com.ijoin.ui.components.SideNavigationRail
import com.ijoin.ui.design.EventCardAccent
import com.ijoin.ui.design.EventCardAccentDark
import com.ijoin.ui.design.EventCardAccentSoft
import com.ijoin.ui.design.EventCardAlert
import com.ijoin.ui.design.EventCardBackground
import com.ijoin.ui.design.EventCardCompactCorner
import com.ijoin.ui.design.EventCardOutline
import com.ijoin.ui.design.EventCardSurface
import com.ijoin.ui.design.EventOutlineButton
import com.ijoin.ui.design.EventPrimaryButton
import com.ijoin.ui.design.EventSectionCard
import com.ijoin.ui.design.EventSurfaceCard
import com.ijoin.ui.design.EventTextAction
import com.ijoin.ui.design.eventBackgroundBrush
import com.ijoin.ui.usercard.UserCardMessage
import com.ijoin.ui.usercard.UserCardViewModel
import com.ijoin.ui.util.formatOrPlaceholder
import com.ijoin.ui.util.resolveMediaUrl

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun UserCardScreen(
    userId: String,
    onBackClick: () -> Unit,
    onOpenEvent: (String) -> Unit,
    onEditProfile: () -> Unit,
    onEditEvent: (Event) -> Unit,
    onOpenFollowingList: (String) -> Unit,
    onOpenUserProfile: (String) -> Unit,
    onOpenBilling: (String) -> Unit,
    onOpenHome: () -> Unit,
    onOpenExplore: () -> Unit,
    onCreateEventClick: () -> Unit,
    onOpenTickets: () -> Unit,
    onOpenProfile: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: UserCardViewModel = remember(userId) { UserCardViewModel(userId) }
) {
    val uiState by viewModel.uiState.collectAsState()
    val profile = uiState.profile
    val strings = strings()
    val snackbarHostState = remember { SnackbarHostState() }
    var pendingDeleteEvent by remember { mutableStateOf<Event?>(null) }
    val platform = remember { getPlatform() }
    var shortcutPromptProfile by remember { mutableStateOf<UserProfile?>(null) }
    val isDesktopWeb = platform.isWeb && !isWebMobile()
    val onTabSelected: (String) -> Unit = { tab ->
        when (tab) {
            "home" -> onOpenHome()
            "explore" -> onOpenExplore()
            "create" -> onCreateEventClick()
            "tickets" -> onOpenTickets()
            "profile" -> onOpenProfile()
        }
    }
    val scaffoldModifier = modifier.fillMaxSize()

    LaunchedEffect(uiState.error, profile) {
        val message = uiState.error ?: return@LaunchedEffect
        if (profile != null) {
            snackbarHostState.showSnackbar(message)
            viewModel.clearError()
        }
    }

    LaunchedEffect(uiState.message) {
        when (val message = uiState.message) {
            UserCardMessage.EventDeleted -> {
                snackbarHostState.showSnackbar(strings.eventDeletedMessage)
                viewModel.clearMessage()
            }
            is UserCardMessage.ShortcutOffer -> {
                if (platform.isMobile && ProfileShortcutManager.isSupported()) {
                    shortcutPromptProfile = message.profile
                }
                viewModel.clearMessage()
            }
            null -> Unit
        }
    }

    val mainScaffold: @Composable () -> Unit = {
        Scaffold(
            modifier = scaffoldModifier,
            containerColor = EventCardBackground,
            topBar = {
                CenterAlignedTopAppBar(
                    title = {
                        val currentProfile = uiState.profile
                        if (currentProfile != null) {
                            Text(currentProfile.displayName())
                        }
                    },
                    navigationIcon = {
                        IconButton(onClick = onBackClick) {
                            Icon(Icons.Default.ArrowBack, contentDescription = null)
                        }
                    },
                    actions = {
                        val currentProfile = uiState.profile
                        if (currentProfile?.self == true) {
                            IconButton(onClick = onEditProfile) {
                                Icon(
                                    imageVector = Icons.Default.Settings,
                                    contentDescription = strings.profile
                                )
                            }
                        }
                    },
                    colors = TopAppBarDefaults.centerAlignedTopAppBarColors(
                        containerColor = EventCardBackground,
                        titleContentColor = EventCardAccentDark,
                        navigationIconContentColor = EventCardAccentDark,
                        actionIconContentColor = EventCardAccentDark
                    )
                )
            },
            snackbarHost = { SnackbarHost(snackbarHostState) },
            bottomBar = {
                if (!isDesktopWeb) {
                    MainBottomNavigationBar(
                        selectedTab = "profile",
                        onTabSelected = onTabSelected
                    )
                }
            }
        ) { paddingValues ->
            when {
                uiState.isLoading -> {
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .background(eventBackgroundBrush())
                            .padding(paddingValues),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator()
                    }
                }

                profile != null -> {
                    UserCardContent(
                        profile = profile,
                        isFollowUpdating = uiState.isFollowUpdating,
                        deletingEventId = uiState.deletingEventId,
                        onFollowClick = viewModel::toggleFollow,
                        onOpenEvent = onOpenEvent,
                        onEditProfile = onEditProfile,
                        onEditEvent = onEditEvent,
                        onDeleteEvent = { event -> pendingDeleteEvent = event },
                        onOpenFollowingList = onOpenFollowingList,
                        onOpenUserProfile = onOpenUserProfile,
                        onOpenBilling = onOpenBilling,
                        strings = strings,
                        modifier = Modifier
                            .fillMaxSize()
                            .background(eventBackgroundBrush())
                            .padding(paddingValues)
                    )
                }

                else -> {
                    UserCardErrorState(
                        message = uiState.error,
                        onRetry = viewModel::refresh,
                        modifier = Modifier
                            .fillMaxSize()
                            .background(eventBackgroundBrush())
                            .padding(paddingValues)
                    )
                }
            }
        }
    }

    if (isDesktopWeb) {
        Row(modifier = Modifier.fillMaxSize()) {
            SideNavigationRail(
                selectedTab = "profile",
                onTabSelected = onTabSelected,
                showAppStoreLinks = true
            )

            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(eventBackgroundBrush()),
                contentAlignment = Alignment.TopCenter
            ) {
                Box(modifier = Modifier.fillMaxWidth(0.6f)) {
                    mainScaffold()
                }
            }
        }
    } else {
        mainScaffold()
    }

    pendingDeleteEvent?.let { event ->
        AlertDialog(
            onDismissRequest = { pendingDeleteEvent = null },
            title = { Text(strings.deleteEventTitle) },
            text = { Text(strings.deleteEventMessage) },
            confirmButton = {
                EventTextAction(
                    text = strings.delete,
                    onClick = {
                        viewModel.deleteEvent(event.id)
                        pendingDeleteEvent = null
                    },
                    contentColor = EventCardAlert
                )
            },
            dismissButton = {
                EventTextAction(
                    text = strings.cancel,
                    onClick = { pendingDeleteEvent = null }
                )
            }
        )
    }

    shortcutPromptProfile?.let { targetProfile ->
        AlertDialog(
            onDismissRequest = { shortcutPromptProfile = null },
            title = { Text(strings.addShortcutTitle) },
            text = { Text(strings.addShortcutMessage(targetProfile.displayName())) },
            confirmButton = {
                EventTextAction(
                    text = strings.addShortcutConfirm,
                    onClick = {
                        val user = targetProfile.user
                        ProfileShortcutManager.requestAddShortcut(
                            ProfileShortcutInfo(
                                userId = user.id,
                                displayName = targetProfile.displayName(),
                                username = user.username,
                                avatarUrl = resolveMediaUrl(user.avatarUrl)
                            )
                        )
                        shortcutPromptProfile = null
                    }
                )
            },
            dismissButton = {
                EventTextAction(
                    text = strings.addShortcutDismiss,
                    onClick = { shortcutPromptProfile = null }
                )
            }
        )
    }
}

@Composable
private fun UserCardContent(
    profile: UserProfile,
    isFollowUpdating: Boolean,
    deletingEventId: String?,
    onFollowClick: () -> Unit,
    onOpenEvent: (String) -> Unit,
    onEditProfile: () -> Unit,
    onEditEvent: (Event) -> Unit,
    onDeleteEvent: (Event) -> Unit,
    onOpenFollowingList: (String) -> Unit,
    onOpenUserProfile: (String) -> Unit,
    onOpenBilling: (String) -> Unit,
    strings: Strings,
    modifier: Modifier = Modifier
) {
    val scrollState = rememberScrollState()
    val totalEvents = profile.upcomingEvents.size + profile.archivedEvents.size
    Column(
        modifier = modifier
            .verticalScroll(scrollState)
            .padding(horizontal = 16.dp, vertical = 12.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        EventSurfaceCard(
            modifier = Modifier.fillMaxWidth(),
            backgroundColor = EventCardSurface,
            borderColor = EventCardOutline
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                UserAvatar(
                    profile = profile,
                    onProfileClick = onEditProfile.takeIf { profile.self }
                )
                Column(
                    verticalArrangement = Arrangement.spacedBy(4.dp),
                    modifier = Modifier.weight(1f)
                ) {
                    Text(
                        text = profile.displayName(),
                        style = MaterialTheme.typography.titleLarge,
                        fontWeight = FontWeight.Bold,
                        modifier = if (profile.self) {
                            Modifier
                                .clip(MaterialTheme.shapes.small)
                                .clickable { onEditProfile() }
                        } else {
                            Modifier
                        }
                    )
                    Text(
                        text = "@${profile.user.username}",
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    profile.user.organizationName?.takeIf { it.isNotBlank() }?.let { organization ->
                        Text(
                            text = organization,
                            style = MaterialTheme.typography.bodyMedium,
                            color = EventCardAccent
                        )
                    }
                }
            }

            StatsRow(
                followers = profile.followersCount,
                following = profile.followingCount,
                events = totalEvents,
                strings = strings,
                onFollowingClick = { onOpenFollowingList(profile.user.id) }
            )

            if (profile.self && profile.user.organizer) {
                OrganizerBillingButton(
                    balance = profile.currentBalance,
                    strings = strings,
                    onClick = { onOpenBilling(profile.user.id) }
                )
            }

            if (profile.followingPreview.isNotEmpty()) {
                FollowingPreviewRow(
                    users = profile.followingPreview,
                    onUserClick = onOpenUserProfile,
                    strings = strings
                )
            }

            if (!profile.self) {
                FollowButton(
                    isFollowing = profile.following,
                    isLoading = isFollowUpdating,
                    onClick = onFollowClick,
                    strings = strings
                )
            }
        }

        SectionCard(
            title = strings.organizerEventsTitle,
            contentPadding = PaddingValues(horizontal = 0.dp)
        ) {
            EventList(
                events = profile.upcomingEvents,
                emptyMessage = strings.userEventsEmpty,
                onOpenEvent = onOpenEvent,
                canManageEvents = profile.self,
                deletingEventId = deletingEventId,
                onEditEvent = onEditEvent,
                onDeleteEvent = onDeleteEvent,
                strings = strings
            )
        }

        SectionCard(
            title = strings.archiveSectionTitle,
            contentPadding = PaddingValues(horizontal = 0.dp)
        ) {
            EventList(
                events = profile.archivedEvents,
                emptyMessage = strings.userArchiveEmpty,
                onOpenEvent = onOpenEvent,
                canManageEvents = profile.self,
                deletingEventId = deletingEventId,
                onEditEvent = onEditEvent,
                onDeleteEvent = onDeleteEvent,
                strings = strings
            )
        }

        SectionCard(title = strings.aboutOrganizerTitle) {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                profile.user.organizationDescription?.takeIf { it.isNotBlank() }?.let { description ->
                    Text(
                        text = description,
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
                ProfileInfoRow(label = strings.email, value = profile.user.email)
                profile.user.phone?.takeIf { it.isNotBlank() }?.let {
                    ProfileInfoRow(label = strings.phone, value = it)
                }
                profile.user.organizationName?.takeIf { it.isNotBlank() }?.let {
                    ProfileInfoRow(label = strings.organizerLabel, value = it)
                }
            }
        }
    }
}

@Composable
private fun UserAvatar(
    profile: UserProfile,
    onProfileClick: (() -> Unit)?
) {
    val avatarUrl = resolveMediaUrl(profile.user.avatarUrl)
    val avatarModifier = Modifier
        .size(72.dp)
        .clip(CircleShape)
        .let { base ->
            if (onProfileClick != null) {
                base.clickable { onProfileClick() }
            } else base
        }

    if (avatarUrl.isNullOrBlank()) {
        Box(
            modifier = avatarModifier.background(EventCardAccentSoft),
            contentAlignment = Alignment.Center
        ) {
            Text(
                text = profile.displayName()
                    .split(" ")
                    .mapNotNull { it.firstOrNull()?.uppercase() }
                    .take(2)
                    .joinToString(""),
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = EventCardAccentDark
            )
        }
    } else {
        AsyncImage(
            model = avatarUrl,
            contentDescription = null,
            modifier = avatarModifier,
            contentScale = ContentScale.Crop
        )
    }
}

@Composable
private fun StatsRow(
    followers: Long,
    following: Long,
    events: Int,
    strings: Strings,
    onFollowingClick: (() -> Unit)? = null
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceEvenly
    ) {
        StatsItem(
            value = followers.toString(),
            label = strings.followersCountLabel
        )
        StatsItem(
            value = following.toString(),
            label = strings.followingCountLabel,
            onClick = onFollowingClick
        )
        StatsItem(
            value = events.toString(),
            label = strings.eventsCountLabel
        )
    }
}

@Composable
private fun StatsItem(
    value: String,
    label: String,
    onClick: (() -> Unit)? = null
) {
    val baseModifier = if (onClick != null) {
        Modifier.clickable { onClick() }
    } else {
        Modifier
    }
    Column(
        modifier = baseModifier,
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Text(
            text = value,
            style = MaterialTheme.typography.titleMedium,
            fontWeight = FontWeight.Bold,
            color = EventCardAccentDark
        )
        Text(
            text = label,
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
    }
}

@Composable
private fun FollowButton(
    isFollowing: Boolean,
    isLoading: Boolean,
    onClick: () -> Unit,
    strings: Strings
) {
    val buttonModifier = Modifier
        .fillMaxWidth()
        .height(48.dp)
    if (isFollowing) {
        EventOutlineButton(
            text = strings.followingAction,
            onClick = onClick,
            enabled = !isLoading,
            isLoading = isLoading,
            leadingIcon = Icons.Default.Check,
            modifier = buttonModifier
        )
    } else {
        EventPrimaryButton(
            text = strings.followAction,
            onClick = onClick,
            enabled = !isLoading,
            isLoading = isLoading,
            leadingIcon = Icons.Default.PersonAdd,
            modifier = buttonModifier
        )
    }
}

@Composable
private fun OrganizerBillingButton(
    balance: MoneyAmount?,
    strings: Strings,
    onClick: () -> Unit
) {
    EventSurfaceCard(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick),
        backgroundColor = EventCardAccentSoft,
        borderColor = EventCardOutline,
        cornerRadius = EventCardCompactCorner,
        contentPadding = PaddingValues(16.dp)
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Icon(
                imageVector = Icons.Default.AccountBalanceWallet,
                contentDescription = null,
                tint = EventCardAccentDark
            )
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(2.dp)
            ) {
                Text(
                    text = strings.openBillingAction,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.SemiBold,
                    color = EventCardAccentDark
                )
                Text(
                    text = "${strings.currentBalanceLabel}: ${balance.formatOrPlaceholder()}",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            Icon(
                imageVector = Icons.Default.ChevronRight,
                contentDescription = null,
                tint = EventCardAccentDark
            )
        }
    }
}

@Composable
private fun SectionCard(
    title: String,
    contentPadding: PaddingValues = PaddingValues(horizontal = 16.dp),
    content: @Composable () -> Unit
) {
    EventSectionCard(
        title = title,
        modifier = Modifier.fillMaxWidth()
    ) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .padding(contentPadding)
        ) {
            content()
        }
    }
}

@Composable
private fun EventList(
    events: List<Event>,
    emptyMessage: String,
    onOpenEvent: (String) -> Unit,
    canManageEvents: Boolean,
    deletingEventId: String?,
    onEditEvent: (Event) -> Unit,
    onDeleteEvent: (Event) -> Unit,
    strings: Strings
) {
    if (events.isEmpty()) {
        Text(
            text = emptyMessage,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
    } else {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            events.forEach { event ->
                EventListItem(
                    event = event,
                    onClick = { onOpenEvent(event.id) },
                    canManageActions = canManageEvents,
                    isDeleting = deletingEventId == event.id,
                    onEditClick = { onEditEvent(event) },
                    onDeleteClick = { onDeleteEvent(event) },
                    strings = strings
                )
            }
        }
    }
}

@Composable
private fun EventListItem(
    event: Event,
    onClick: () -> Unit,
    canManageActions: Boolean,
    isDeleting: Boolean,
    onEditClick: () -> Unit,
    onDeleteClick: () -> Unit,
    strings: Strings
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        RecommendedEventCard(
            event = event,
            onEventClick = { onClick() },
            onJoinClick = { onClick() }
        )
        if (canManageActions) {
            EventManagementButtons(
                onEditClick = onEditClick,
                onDeleteClick = onDeleteClick,
                isDeleting = isDeleting,
                strings = strings
            )
        }
    }
}

@Composable
private fun FollowingPreviewRow(
    users: List<User>,
    onUserClick: (String) -> Unit,
    strings: Strings
) {
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Text(
            text = strings.followingCountLabel,
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        LazyRow(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            items(users, key = { it.id }) { user ->
                FollowingPreviewAvatar(
                    user = user,
                    onClick = { onUserClick(user.id) }
                )
            }
        }
    }
}

@Composable
private fun FollowingPreviewAvatar(
    user: User,
    onClick: () -> Unit
) {
    val avatarUrl = resolveMediaUrl(user.avatarUrl)
    val initials = listOfNotNull(user.firstName, user.lastName)
        .filter { it.isNotBlank() }
        .flatMap { it.split(" ") }
        .mapNotNull { it.firstOrNull()?.uppercase() }
        .take(2)
        .joinToString("")
        .ifBlank { user.username.take(2).uppercase() }

    Box(
        modifier = Modifier
            .size(56.dp)
            .clip(CircleShape)
            .clickable(onClick = onClick),
        contentAlignment = Alignment.Center
    ) {
        if (avatarUrl.isNullOrBlank()) {
            Box(
                modifier = Modifier
                    .matchParentSize()
                    .background(EventCardAccentSoft),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = initials,
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.Bold,
                    color = EventCardAccentDark
                )
            }
        } else {
            AsyncImage(
                model = avatarUrl,
                contentDescription = null,
                modifier = Modifier.matchParentSize(),
                contentScale = ContentScale.Crop
            )
        }
    }
}

@Composable
private fun ProfileInfoRow(label: String, value: String) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Text(
            text = "$label:",
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        Text(
            text = value,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurface
        )
    }
}

@Composable
private fun UserCardErrorState(
    message: String?,
    onRetry: () -> Unit,
    modifier: Modifier = Modifier
) {
    val strings = strings()
    Column(
        modifier = modifier.padding(24.dp),
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Text(
            text = message ?: strings.profileLoadError,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            modifier = Modifier.padding(bottom = 16.dp)
        )
        EventPrimaryButton(
            text = strings.retry,
            onClick = onRetry
        )
    }
}

private fun UserProfile.displayName(): String {
    val firstLast = listOfNotNull(user.firstName, user.lastName)
        .filter { it.isNotBlank() }
        .joinToString(" ")
    return if (firstLast.isNotBlank()) firstLast else user.username
}
